/**
 * App shell — wires the Auth provider, the role-aware router and the
 * top-level navigation skeleton.
 */
import type { ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "./auth/AuthContext";
import { RoleGuard } from "./auth/RoleGuard";
import { AppShell } from "./layout/AppShell";
import { SystemStatusProvider } from "./system/SystemStatusContext";
import { ThemeProvider } from "./system/ThemeContext";
import { CopilotProvider } from "./system/CopilotContext";
import { ToastProvider } from "./system/ToastContext";
import { LoginView } from "./views/Login";
import { OAuthCallback } from "./views/OAuthCallback";
import { ExecutiveCenter } from "./views/ExecutiveCenter";
import { AlertCenter } from "./views/AlertCenter";
import { PriorityCases } from "./views/PriorityCases";
import { OperationalMap } from "./views/OperationalMap";
import { DataQuality } from "./views/DataQuality";
import { EconomicImpact } from "./views/EconomicImpact";
import { Prediction } from "./views/Prediction";
import { Crews } from "./views/Crews";
import { Observability } from "./views/Observability";
import { Insights } from "./views/Insights";
import { LiveOperations } from "./views/LiveOperations";
import { LoraIntelligence } from "./views/LoraIntelligence";
import { MvpProgram } from "./views/MvpProgram";
import { CommercialView } from "./views/Commercial";
import { LeakAlertView } from "./views/LeakAlert";
import { RegulatoryView } from "./views/Regulatory";
import {
  DmasPage,
  TerrenoPage,
  SlaPage,
  EvidenciasPage,
  RedHidraulicaPage,
  ValvulasPage,
  PresionPage,
  PrvsPage,
  CoberturaPage,
  HeatmapsPage,
  IntegracionesPage,
  UsuariosPage,
} from "./views/ScaffoldPages";
import { OperadorDashboards } from "./views/dashboards/OperadorDashboards";
import {
  AnalistaBalance,
  AnalistaReportes,
  AnalistaWhatIf,
} from "./views/AnalistaNRW";
import { InspectorOrdenes } from "./views/Inspector";
import {
  SupervisorAuditoria,
  SupervisorCatastro,
  SupervisorParametros,
} from "./views/Supervisor";
import { useAuth, ROLE_META } from "./auth/AuthContext";
import type { Role } from "./auth/AuthContext";

/**
 * Redirección de la raíz según el rol activo: cada persona aterriza en su
 * home dedicado. El Gerente (home "/") ve el Centro Ejecutivo directamente.
 */
function HomeRedirect(): JSX.Element {
  const { activeRole } = useAuth();
  const home = activeRole ? ROLE_META[activeRole].home : "/";
  if (!activeRole || home === "/") {
    return <ExecutiveCenter />;
  }
  return <Navigate to={home} replace />;
}

interface ShellGuardProps {
  allow: Role[];
  children: ReactNode;
}

function ShellGuard({ allow, children }: ShellGuardProps): JSX.Element {
  return (
    <RoleGuard allow={allow}>
      <AppShell>{children}</AppShell>
    </RoleGuard>
  );
}

const ALL_ROLES: Role[] = [
  "Gerente",
  "Comercial",
  "Operador",
  "Analista_NRW",
  "IngenieroRF",
  "AnalistaFugas",
  "Inspector",
  "Supervisor",
  "Admin",
  "Regulatorio",
];

// Grupos semánticos de acceso por persona.
const ANALITICA: Role[] = ["Analista_NRW", "Supervisor", "Admin"];
const ANALITICA_GERENCIA: Role[] = ["Gerente", "Analista_NRW", "Supervisor", "Admin"];
const ALERTAS_ROLES: Role[] = [
  "Gerente",
  "Comercial",
  "Operador",
  "Analista_NRW",
  "AnalistaFugas",
  "Supervisor",
  "Admin",
];
const CASOS_ROLES: Role[] = [
  "Gerente",
  "Comercial",
  "Operador",
  "Analista_NRW",
  "AnalistaFugas",
  "Supervisor",
  "Admin",
];
const RED_ROLES: Role[] = ["Analista_NRW", "IngenieroRF", "Supervisor", "Admin"];
const RF_ROLES: Role[] = ["IngenieroRF", "Analista_NRW", "Supervisor", "Admin"];
const TERRENO_ROLES: Role[] = ["Inspector", "Supervisor", "Admin"];
const PLATAFORMA: Role[] = ["Supervisor", "Admin"];

export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <SystemStatusProvider>
            <CopilotProvider>
              <Routes>
            <Route path="/login" element={<LoginView />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route
              path="/"
              element={
                <ShellGuard allow={ALL_ROLES}>
                  <HomeRedirect />
                </ShellGuard>
              }
            />
            <Route
              path="/alertas"
              element={
                <ShellGuard allow={ALERTAS_ROLES}>
                  <AlertCenter />
                </ShellGuard>
              }
            />
            <Route
              path="/mapa"
              element={
                <ShellGuard allow={ALL_ROLES}>
                  <OperationalMap />
                </ShellGuard>
              }
            />
            <Route
              path="/calidad"
              element={
                <ShellGuard allow={["Analista_NRW", "Supervisor", "Admin", "Regulatorio"]}>
                  <DataQuality />
                </ShellGuard>
              }
            />
            <Route
              path="/comercial"
              element={
                <ShellGuard allow={["Gerente", "Comercial", "Supervisor", "Admin"]}>
                  <CommercialView />
                </ShellGuard>
              }
            />
            <Route
              path="/fugas"
              element={
                <ShellGuard allow={["AnalistaFugas", "Operador", "Supervisor", "Admin"]}>
                  <LeakAlertView />
                </ShellGuard>
              }
            />
            <Route
              path="/regulatorio"
              element={
                <ShellGuard allow={["Regulatorio", "Gerente", "Supervisor", "Admin"]}>
                  <RegulatoryView />
                </ShellGuard>
              }
            />
            <Route
              path="/casos"
              element={
                <ShellGuard allow={CASOS_ROLES}>
                  <PriorityCases />
                </ShellGuard>
              }
            />
            <Route
              path="/impacto"
              element={
                <ShellGuard allow={ANALITICA_GERENCIA}>
                  <EconomicImpact />
                </ShellGuard>
              }
            />
            <Route
              path="/mvp"
              element={
                <ShellGuard allow={["Gerente", "Operador", "Analista_NRW", "Supervisor", "Admin"]}>
                  <MvpProgram />
                </ShellGuard>
              }
            />
            <Route
              path="/prediccion"
              element={
                <ShellGuard allow={ANALITICA}>
                  <Prediction />
                </ShellGuard>
              }
            />
            <Route
              path="/dmas"
              element={
                <ShellGuard allow={ANALITICA}>
                  <DmasPage />
                </ShellGuard>
              }
            />
            <Route
              path="/insights"
              element={
                <ShellGuard allow={CASOS_ROLES}>
                  <Insights />
                </ShellGuard>
              }
            />
            <Route
              path="/live"
              element={
                <ShellGuard allow={ALL_ROLES}>
                  <LiveOperations />
                </ShellGuard>
              }
            />
            <Route
              path="/terreno"
              element={
                <ShellGuard allow={TERRENO_ROLES}>
                  <TerrenoPage />
                </ShellGuard>
              }
            />
            <Route
              path="/cuadrillas"
              element={
                <ShellGuard allow={["Supervisor", "Admin"]}>
                  <Crews />
                </ShellGuard>
              }
            />
            <Route
              path="/sla"
              element={
                <ShellGuard allow={["Supervisor", "Admin", "Gerente"]}>
                  <SlaPage />
                </ShellGuard>
              }
            />
            <Route
              path="/evidencias"
              element={
                <ShellGuard allow={["Inspector", "Supervisor", "Admin", "Regulatorio"]}>
                  <EvidenciasPage />
                </ShellGuard>
              }
            />
            <Route
              path="/red"
              element={
                <ShellGuard allow={RED_ROLES}>
                  <RedHidraulicaPage />
                </ShellGuard>
              }
            />
            <Route
              path="/valvulas"
              element={
                <ShellGuard allow={RED_ROLES}>
                  <ValvulasPage />
                </ShellGuard>
              }
            />
            <Route
              path="/presion"
              element={
                <ShellGuard allow={RED_ROLES}>
                  <PresionPage />
                </ShellGuard>
              }
            />
            <Route
              path="/prvs"
              element={
                <ShellGuard allow={RED_ROLES}>
                  <PrvsPage />
                </ShellGuard>
              }
            />
            <Route
              path="/cobertura"
              element={
                <ShellGuard allow={RF_ROLES}>
                  <CoberturaPage />
                </ShellGuard>
              }
            />
            <Route
              path="/lora"
              element={
                <ShellGuard allow={RF_ROLES}>
                  <LoraIntelligence />
                </ShellGuard>
              }
            />
            <Route
              path="/heatmaps"
              element={
                <ShellGuard allow={["Analista_NRW", "IngenieroRF", "Supervisor", "Admin"]}>
                  <HeatmapsPage />
                </ShellGuard>
              }
            />
            <Route
              path="/integraciones"
              element={
                <ShellGuard allow={PLATAFORMA}>
                  <IntegracionesPage />
                </ShellGuard>
              }
            />
            <Route
              path="/observabilidad"
              element={
                <ShellGuard allow={["IngenieroRF", "Analista_NRW", "Supervisor", "Admin"]}>
                  <Observability />
                </ShellGuard>
              }
            />
            <Route
              path="/usuarios"
              element={
                <ShellGuard allow={PLATAFORMA}>
                  <UsuariosPage />
                </ShellGuard>
              }
            />
            <Route
              path="/operador/dashboards"
              element={
                <ShellGuard allow={["Operador", "Analista_NRW", "Supervisor", "Admin", "Gerente"]}>
                  <OperadorDashboards />
                </ShellGuard>
              }
            />
            <Route
              path="/analista/balance"
              element={
                <ShellGuard allow={ANALITICA}>
                  <AnalistaBalance />
                </ShellGuard>
              }
            />
            <Route
              path="/analista/whatif"
              element={
                <ShellGuard allow={ANALITICA}>
                  <AnalistaWhatIf />
                </ShellGuard>
              }
            />
            <Route
              path="/analista/reportes"
              element={
                <ShellGuard allow={["Analista_NRW", "Supervisor", "Admin", "Regulatorio"]}>
                  <AnalistaReportes />
                </ShellGuard>
              }
            />
            <Route
              path="/inspector/ordenes"
              element={
                <ShellGuard allow={TERRENO_ROLES}>
                  <InspectorOrdenes />
                </ShellGuard>
              }
            />
            <Route
              path="/supervisor/catastro"
              element={
                <ShellGuard allow={["Supervisor", "Admin", "Comercial"]}>
                  <SupervisorCatastro />
                </ShellGuard>
              }
            />
            <Route
              path="/supervisor/parametros"
              element={
                <ShellGuard allow={PLATAFORMA}>
                  <SupervisorParametros />
                </ShellGuard>
              }
            />
            <Route
              path="/supervisor/auditoria"
              element={
                <ShellGuard allow={["Supervisor", "Admin", "Regulatorio"]}>
                  <SupervisorAuditoria />
                </ShellGuard>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CopilotProvider>
        </SystemStatusProvider>
      </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
